// The default behavior of .toLocaleString is to round to 3 digits, this felt a bit too little so I set it to 5
import { joinDefinedStrings } from './strings'

// Might be worth investigating what a reasonable number is
export const decimalToLocalizedString = (
    number: number,
    isInteger = false,
    maximumNumberOfDigits = 5,
    minimumNumberOfDigits?: number,
    locale = 'de-DE'
) => {
    return number.toLocaleString(locale, {
        style: 'decimal',
        minimumFractionDigits: isInteger ? 0 : minimumNumberOfDigits,
        maximumFractionDigits: isInteger ? 0 : maximumNumberOfDigits,
    })
}

export const decimalToGermanString = (number: number, isInteger = false, maximumNumberOfDigits = 5, minimumNumberOfDigits?: number) =>
    decimalToLocalizedString(number, isInteger, maximumNumberOfDigits, minimumNumberOfDigits, 'de-DE')

export function formatNumberWithAbbreviation(
    num: number,
    maxDecimals = 2,
    alwaysAbbreviate = false,
    minimumNumberOfDigits?: number,
    locale = 'de-DE'
): string {
    const abbreviations = getAbbreviations(locale)
    const thousandAbbreviation = abbreviations.at(-1)?.[0] ?? ''

    // If the number is less than 10.000, we don't need an abbreviation unless explicitely set
    if (num < 1e4) {
        if (alwaysAbbreviate) {
            if (num < 1e3) {
                return `< 1 ${thousandAbbreviation}`
            }
            const formattedNumber =
                minimumNumberOfDigits === undefined
                    ? decimalToLocalizedString(num / 1e3, false, maxDecimals, 0, locale)
                    : decimalToLocalizedString(num / 1e3, false, maxDecimals, minimumNumberOfDigits, locale)
            return `${formattedNumber} ${thousandAbbreviation}`
        }
        return decimalToLocalizedString(num, false, maxDecimals, minimumNumberOfDigits, locale)
    }

    for (const [abbr, value] of abbreviations) {
        if (num >= value) {
            const formattedNumber =
                minimumNumberOfDigits === undefined
                    ? decimalToLocalizedString(num / value, false, maxDecimals, 0, locale)
                    : decimalToLocalizedString(num / value, false, maxDecimals, minimumNumberOfDigits, locale)
            return `${formattedNumber} ${abbr}`
        }
    }
    // Not needed except for typing
    return decimalToLocalizedString(num, false, maxDecimals, undefined, locale)
}

export function getFormattedValueAndUnitFromNumber(
    num?: number,
    unit?: string,
    numberOfDigits?: number,
    locale = 'de-DE'
): { value: string; unit?: string } {
    if (num === undefined) {
        return { value: '-' }
    }

    const [shortNumber, abbreviation] = formatNumberWithAbbreviation(num, numberOfDigits, false, numberOfDigits, locale).split(' ')
    return {
        value: shortNumber,
        unit: joinDefinedStrings([abbreviation, unit]),
    }
}

export function formatNumberWithShortAbbreviation(num: number, maxDecimals = 2, locale = 'de-DE'): string {
    const abbreviations = getShortAbbreviations(locale)

    // If the number is less than 10.000, we don't need an abbreviation.
    if (num < 1e4) {
        return decimalToLocalizedString(num, false, maxDecimals, undefined, locale)
    }

    for (const [shortAbbr, value] of abbreviations) {
        if (num >= value) {
            const formattedNumber = decimalToLocalizedString(num / value, false, maxDecimals, 0, locale)
            return `${formattedNumber}${shortAbbr}`
        }
    }
    // Not needed except for typing
    return decimalToLocalizedString(num, false, maxDecimals, undefined, locale)
}

function getAbbreviations(locale: string): [string, number][] {
    if (locale === 'en-US') {
        return [
            ['T', 1e12],
            ['B', 1e9],
            ['M', 1e6],
            ['K', 1e3],
        ]
    }

    return [
        ['Bio', 1e12],
        ['Mrd', 1e9],
        ['Mio', 1e6],
        ['Tsd', 1e3],
    ]
}

function getShortAbbreviations(locale: string): [string, number][] {
    if (locale === 'en-US') {
        return [
            ['T', 1e12],
            ['B', 1e9],
            ['M', 1e6],
            ['K', 1e3],
        ]
    }

    return [
        ['B', 1e12],
        ['Md', 1e9],
        ['M', 1e6],
        ['T', 1e3],
    ]
}

export function pixelToNumber(pixelValue: `${number}px`): number {
    return parseInt(pixelValue)
}
